import { normalizarTexto } from '../utils/textoBusqueda';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Shield, CheckCircle } from 'lucide-react';
import { X, User, Eye, EyeOff, ArrowLeft, Settings, FileText } from './icons';
import StepWizard from './StepWizard';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getRoleLabel } from '../utils/roleLabels';

export default function CreateAuthUserModal({ isOpen, onClose, onUserCreated }) {
    const { user: currentUser } = useAuth();
    const [step, setStep] = useState(1);
    const [creationType, setCreationType] = useState(''); // 'standalone' o 'nomina'
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'usuario',
        isActive: true,
        // Campos adicionales para usuario de nómina
        nominaId: null,
        nombre: '',
        apellido: '',
        email: '',
        rut: '',
        departamento: '',
        empresa: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [nominaUsers, setNominaUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [generatedUsername, setGeneratedUsername] = useState('');

    useEffect(() => {
        if (isOpen) {
            // Solo resetear cuando se abre el modal, no cuando ya está abierto
            setFormData({
                username: '',
                password: '',
                role: 'usuario',
                isActive: true,
                nominaId: null,
                nombre: '',
                apellido: '',
                email: '',
                rut: '',
                departamento: '',
                empresa: ''
            });
            setError('');
            setStep(1);
            setCreationType('');
            setSearchTerm('');
            setSelectedUser(null);
            setGeneratedUsername('');
        }
    }, [isOpen]);

    // Scroll automático cuando cambia el step
    useEffect(() => {
        if (isOpen && step > 1) {
            // Pequeño delay para asegurar que el contenido se haya renderizado
            setTimeout(() => {
                const modal = document.querySelector('[data-modal="create-user"]');
                if (modal && modal.scrollHeight > modal.clientHeight) {
                    // Solo hacer scroll si hay contenido que se puede desplazar
                    modal.scrollTo({ top: modal.scrollHeight, behavior: 'smooth' });
                }
            }, 150);
        }
    }, [step, isOpen]);

    const loadNominaUsers = async () => {
        try {
            const response = await api.get('/pazysalvoroles/users');
            // Mapeamos authUserId a hasAuthUser para mantener la compatibilidad con el resto del componente
            const mappedUsers = response.data.map(user => ({
                ...user,
                hasAuthUser: !!user.authUserId
            }));
            setNominaUsers(mappedUsers);
            setError(''); // Limpiar errores al cargar exitosamente
        } catch (err) {
            console.error('Error al cargar usuarios de nómina:', err);
            setError('Error al cargar usuarios de nómina');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (step === 1 && !creationType) {
            setError('Por favor selecciona un tipo de usuario');
            return; // No hacer nada si no se ha seleccionado el tipo
        }

        if (step < 3) {
            setError(''); // Limpiar errores al avanzar
            setStep(s => s + 1);
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Usar el mismo endpoint para ambos tipos de usuario
            const isStandalone = creationType === 'standalone';
            const userData = {
                // Username obligatorio para independiente; para nómina usar generado si no viene
                username: isStandalone
                    ? (formData.username || '').trim()
                    : (formData.username || generatedUsername || '').trim(),
                // Para usuarios standalone NO enviar RUT para evitar que coincida con usuarios de nómina
                rut: isStandalone
                    ? ''  // No enviar RUT para usuarios independientes
                    : (formData.rut || selectedUser?.rut || ''),
                // Solo usar email de selectedUser para usuarios de nómina
                email: isStandalone
                    ? (formData.email || '')
                    : (formData.email || selectedUser?.email || ''),
                password: formData.password,
                nombre: isStandalone
                    ? (formData.nombre || 'Usuario')
                    : (formData.nombre || selectedUser?.nombre || 'Usuario'),
                apellido: isStandalone
                    ? (formData.apellido || 'Independiente')
                    : (formData.apellido || selectedUser?.apellido || 'Independiente'),
                departamento: isStandalone
                    ? (formData.departamento || 'TI')
                    : (formData.departamento || selectedUser?.departamento || 'TI'),
                empresa: isStandalone
                    ? (formData.empresa || 'VICSA')
                    : (formData.empresa || selectedUser?.empresa || 'VICSA'),
                role: (formData.role || 'usuario').toLowerCase()
            };

            // Backend expone /auth/create-independent-user
            await api.post('/auth/create-independent-user', userData);
            onUserCreated();
            onClose();
        } catch (err) {
            console.error('Error al crear usuario:', err);
            const errorMessage = err.response?.data?.message || err.response?.data?.title || err.message || 'Error al crear usuario';
            setError(errorMessage);
            // No cerrar el modal en caso de error, solo mostrar el mensaje
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCreationTypeSelect = (type) => {
        setCreationType(type);
        if (type === 'nomina') {
            loadNominaUsers();
        } else {
            // Limpiar selectedUser al cambiar a standalone para evitar mezcla de datos
            setSelectedUser(null);
            setGeneratedUsername('');
        }
        // Avanzar automáticamente al siguiente paso
        setStep(2);
        setError(''); // Limpiar errores al avanzar
    };

    const handleNominaUserSelect = (user) => {
        setSelectedUser(user);

        // Si el usuario de nómina ya tiene username, usarlo; si no, generar uno nuevo
        let username = '';
        if (user.username && user.username.trim()) {
            // Usar el username existente del usuario de nómina
            username = user.username.trim();
        } else {
            // Generar username automáticamente (primera letra del nombre + apellido)
            const generateUsername = (nombre, apellido) => {
                if (!nombre || !apellido) return '';
                const firstLetter = nombre.charAt(0).toLowerCase();
                const apellidoLower = apellido.toLowerCase().replace(/\s+/g, '');
                return `${firstLetter}${apellidoLower}`;
            };
            username = generateUsername(user.nombre, user.apellido);
        }

        setGeneratedUsername(username);

        setFormData(prev => ({
            ...prev,
            nominaId: user.id,
            nombre: user.nombre,
            apellido: user.apellido,
            email: user.email,
            username: username,
            rut: user.rut,
            departamento: user.departamento,
            empresa: user.empresa
        }));
    };

    // Filtrar usuarios y aplicar búsqueda
    const filteredNominaUsers = nominaUsers
        // Mostrar todos los usuarios, pero marcando los ya registrados
        .filter(user => {
            if (!searchTerm) return true;
            const searchLower = searchTerm.toLowerCase();
            return (
                normalizarTexto(user.nombre).includes(normalizarTexto(searchLower)) ||
                normalizarTexto(user.apellido).includes(normalizarTexto(searchLower)) ||
                normalizarTexto(user.email).includes(normalizarTexto(searchLower)) ||
                normalizarTexto(user.departamento).includes(normalizarTexto(searchLower)) ||
                normalizarTexto(user.empresa).includes(normalizarTexto(searchLower)) ||
                normalizarTexto(user.rut).includes(normalizarTexto(searchLower))
            );
        });

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
        >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-modal="create-user">
                {/* Header */}
                <div className="p-4 relative">
                    {/* Botón X en esquina superior derecha */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <div className="flex flex-col items-center">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1 text-center">
                            Crear Nuevo Usuario
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 text-center text-sm">
                            Completa la información para registrar un nuevo usuario
                        </p>
                    </div>
                </div>

                {/* Step Wizard */}
                <StepWizard current={step} type="usuario" />

                {/* Form Content */}
                <div className="p-4">
                    {error && (
                        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg dark:bg-red-900 dark:border-red-700 dark:text-red-200">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {step === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                                        <User className="w-4 h-4 mr-2 text-blue-600" />
                                        Selecciona el tipo de usuario a crear
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Opción 1: Usuario independiente */}
                                        <div
                                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${creationType === 'standalone'
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                }`}
                                            onClick={() => handleCreationTypeSelect('standalone')}
                                        >
                                            <div className="flex items-center mb-4">
                                                <User className="w-8 h-8 text-blue-600 mr-3" />
                                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    Usuario Independiente
                                                </h4>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                                Crear un usuario sin vincular a datos de nómina. Ideal para usuarios externos o administrativos.
                                            </p>
                                        </div>

                                        {/* Opción 2: Usuario de nómina */}
                                        <div
                                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${creationType === 'nomina'
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                }`}
                                            onClick={() => handleCreationTypeSelect('nomina')}
                                        >
                                            <div className="flex items-center mb-4">
                                                <FileText className="w-8 h-8 text-green-600 mr-3" />
                                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    Usuario de Nómina
                                                </h4>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                                Crear un usuario vinculado a los datos de nómina existentes. Los datos se cargarán automáticamente.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-6">
                                {creationType === 'standalone' && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                            <User className="w-5 h-5 mr-2 text-blue-600" />
                                            Información del usuario independiente
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Nombre */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Nombre *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="nombre"
                                                    value={formData.nombre}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="Ingresa el nombre"
                                                />
                                            </div>

                                            {/* Apellido */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Apellido *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="apellido"
                                                    value={formData.apellido}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="Ingresa el apellido"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Nombre de Usuario *
                                                </label>
                                                <div className="relative">
                                                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        name="username"
                                                        value={formData.username}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Ingresa el nombre de usuario"
                                                    />
                                                </div>
                                            </div>

                                            {/* Email (opcional) */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Email (opcional)
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="Ingresa el email (opcional)"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Contraseña *
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-full pl-3 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Ingresa la contraseña"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    >
                                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Rol del Usuario *
                                                </label>
                                                <div className="relative">
                                                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                    <select
                                                        name="role"
                                                        value={formData.role}
                                                        onChange={handleInputChange}
                                                        className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    >
                                                        {currentUser?.role === 'admin' ? (
                                                            <>
                                                                <option value="admin">Administrador</option>
                                                                <option value="soporte">Staff</option>
                                                                <option value="usuario">Usuario</option>
                                                            </>
                                                        ) : (
                                                            <option value="usuario">Usuario</option>
                                                        )}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-3 md:col-span-2">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleInputChange}
                                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Usuario activo
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {creationType === 'nomina' && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                            <FileText className="w-5 h-5 mr-2 text-green-600" />
                                            Selecciona el usuario de nómina
                                        </h3>
                                        <div className="space-y-4">
                                            {/* Barra de búsqueda */}
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Buscar por nombre, email, departamento, empresa o RUT..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </div>

                                            {/* Contador de resultados */}
                                            {!selectedUser ? (
                                                <>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        {filteredNominaUsers.length} usuario{filteredNominaUsers.length !== 1 ? 's' : ''} disponible{filteredNominaUsers.length !== 1 ? 's' : ''}
                                                        {searchTerm && ` para "${searchTerm}"`}
                                                    </div>

                                                    <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                                                        {filteredNominaUsers.length === 0 ? (
                                                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                                                {searchTerm ? 'No se encontraron usuarios con ese criterio de búsqueda' : 'No hay usuarios de nómina disponibles para registrar'}
                                                            </div>
                                                        ) : (
                                                            filteredNominaUsers.map((user) => (
                                                                <div
                                                                    key={user.id}
                                                                    className={`p-4 border-b border-gray-200 dark:border-gray-600 transition-colors ${user.hasAuthUser
                                                                        ? 'bg-gray-50 dark:bg-gray-800/50 opacity-60 cursor-not-allowed'
                                                                        : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
                                                                        }`}
                                                                    onClick={() => !user.hasAuthUser && handleNominaUserSelect(user)}
                                                                >
                                                                    <div className="flex items-center justify-between">
                                                                        <div>
                                                                            <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                                                                {user.nombre} {user.apellido}
                                                                                {user.hasAuthUser && (
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                                                        Ya registrado
                                                                                    </span>
                                                                                )}
                                                                            </h4>
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                                {user.email} • {user.departamento} • {user.empresa}
                                                                            </p>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                RUT: {user.rut}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                            Usuario Seleccionado
                                                        </h4>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedUser(null);
                                                                setGeneratedUsername('');
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    nominaId: null,
                                                                    nombre: '',
                                                                    apellido: '',
                                                                    email: '',
                                                                    username: '',
                                                                    rut: '',
                                                                    departamento: '',
                                                                    empresa: ''
                                                                }));
                                                            }}
                                                            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                        >
                                                            Cambiar usuario
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Nombre Completo
                                                            </label>
                                                            <p className="text-gray-900 dark:text-white font-medium">
                                                                {selectedUser.nombre} {selectedUser.apellido}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Email
                                                            </label>
                                                            <p className="text-gray-900 dark:text-white">
                                                                {selectedUser.email}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                RUT
                                                            </label>
                                                            <p className="text-gray-900 dark:text-white">
                                                                {selectedUser.rut}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Departamento
                                                            </label>
                                                            <p className="text-gray-900 dark:text-white">
                                                                {selectedUser.departamento}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Empresa
                                                            </label>
                                                            <p className="text-gray-900 dark:text-white">
                                                                {selectedUser.empresa}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Username Generado
                                                            </label>
                                                            <p className="text-blue-600 dark:text-blue-400 font-medium">
                                                                {generatedUsername}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Contraseña *
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type={showPassword ? 'text' : 'password'}
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="w-full pl-3 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            placeholder="Ingresa la contraseña"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Rol del Usuario *
                                                    </label>
                                                    <div className="relative">
                                                        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                        <select
                                                            name="role"
                                                            value={formData.role}
                                                            onChange={handleInputChange}
                                                            className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        >
                                                            {currentUser?.role === 'admin' ? (
                                                                <>
                                                                    <option value="admin">Administrador</option>
                                                                    <option value="soporte">Staff</option>
                                                                    <option value="usuario">Usuario</option>
                                                                </>
                                                            ) : (
                                                                <option value="usuario">Usuario</option>
                                                            )}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {step === 3 && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                                        Revisión y confirmación
                                    </h3>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Usuario:</span>
                                                <p className="text-gray-900 dark:text-white">{formData.username}</p>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Rol:</span>
                                                <p className="text-gray-900 dark:text-white">{getRoleLabel(formData.role)}</p>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Estado:</span>
                                                <p className="text-gray-900 dark:text-white">{formData.isActive ? 'Activo' : 'Inactivo'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex justify-between pt-4">
                            <button
                                type="button"
                                onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
                                className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                {step > 1 ? 'Anterior' : 'Cancelar'}
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                {loading ? 'Creando...' : step < 3 ? 'Siguiente' : 'Crear Usuario'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}